import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { checkPickLevels, type Instrument, type PickDetail, type PickDirection } from "@alpha/shared";
import { api, errorText } from "../api";
import { InstrumentSearch } from "../components/InstrumentSearch";
import { PositionSizeHelper } from "../components/PositionSizeHelper";
import { Empty, ErrorBox, Loading, PageHeader } from "../components/ui";
import { useMe } from "../me";
import { useAsync } from "../useAsync";

interface Values {
  direction: PickDirection;
  entry: string;
  target: string;
  stop: string;
  riskPerTradePct: string;
  thesis: string;
}

const BLANK: Values = { direction: "long", entry: "", target: "", stop: "", riskPerTradePct: "1", thesis: "" };

export function PickFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const editing = id !== undefined;

  const existing = useAsync(
    (s) => (editing ? api.get<PickDetail>(`/v1/picks/${id}`, s) : Promise.resolve(null)),
    [id],
  );
  const preset = params.get("instrument");
  const presetInst = useAsync(
    (s) => (!editing && preset ? api.get<Instrument>(`/v1/instruments/${preset}`, s) : Promise.resolve(null)),
    [preset, editing],
  );

  if (existing.loading || presetInst.loading) return <Loading />;
  if (existing.error) return <ErrorBox error={existing.error} onRetry={existing.reload} />;
  return <PickForm key={id ?? "new"} existing={existing.data} presetInstrument={presetInst.data} />;
}

function PickForm({ existing, presetInstrument }: { existing: PickDetail | null; presetInstrument: Instrument | null }) {
  const me = useMe();
  const navigate = useNavigate();
  const [instrument, setInstrument] = useState<{ id: string; ticker: string } | null>(
    existing ? { id: existing.instrumentId, ticker: existing.ticker } : presetInstrument,
  );
  const [v, setV] = useState<Values>(
    existing
      ? {
          direction: existing.direction,
          entry: existing.entry,
          target: existing.target,
          stop: existing.stop,
          riskPerTradePct: existing.riskPerTradePct,
          thesis: existing.thesis,
        }
      : BLANK,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (existing && (existing.authorId !== me.id || existing.status !== "draft")) {
    return (
      <section>
        <PageHeader title="Edit pick" />
        <Empty>Only the author can edit a pick, and only while it is a draft (private or internal).</Empty>
        <Link to={`/picks/${existing.id}`}>Back to the pick</Link>
      </section>
    );
  }

  const set = (k: keyof Values) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const levelsMsg =
    v.entry && v.target && v.stop ? checkPickLevels(v.direction, v.entry, v.target, v.stop) : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!instrument) {
      setError("Choose an instrument first.");
      return;
    }
    if (levelsMsg) {
      setError(levelsMsg);
      return;
    }
    setBusy(true);
    try {
      const saved = existing
        ? await api.patch<{ id: string }>(`/v1/picks/${existing.id}`, { ...v, version: existing.version })
        : await api.post<{ id: string }>("/v1/picks", { instrumentId: instrument.id, ...v });
      navigate(`/picks/${saved.id}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <section>
      <PageHeader title={existing ? `Edit pick ${existing.ticker}` : "New pick"} />
      <div className="split">
        <form className="card grow stack" onSubmit={submit} noValidate>
          <div className="field">
            <span className="label">Instrument</span>
            {instrument ? (
              <div className="row">
                <strong>{instrument.ticker}</strong>
                {!existing && (
                  <button type="button" className="btn small" onClick={() => setInstrument(null)}>
                    Change
                  </button>
                )}
              </div>
            ) : (
              <InstrumentSearch onSelect={(i) => setInstrument({ id: i.id, ticker: i.ticker })} />
            )}
          </div>
          <div className="grid4">
            <label>
              Direction
              <select value={v.direction} onChange={set("direction")}>
                <option value="long">Long</option>
                <option value="short">Short</option>
              </select>
            </label>
            <label>
              Entry
              <input inputMode="decimal" value={v.entry} onChange={set("entry")} required />
            </label>
            <label>
              Target
              <input inputMode="decimal" value={v.target} onChange={set("target")} required />
            </label>
            <label>
              Stop
              <input inputMode="decimal" value={v.stop} onChange={set("stop")} required />
            </label>
          </div>
          <label>
            Risk per trade (percent of account)
            <input inputMode="decimal" value={v.riskPerTradePct} onChange={set("riskPerTradePct")} required />
          </label>
          <label>
            Thesis (plain text)
            <textarea rows={8} maxLength={5000} value={v.thesis} onChange={set("thesis")} required />
          </label>
          {levelsMsg && <p className="warn">{levelsMsg}</p>}
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <div className="row">
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "Saving..." : existing ? "Save changes" : "Create as private draft"}
            </button>
            <button type="button" className="btn" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
          {!existing && <p className="muted">New picks start as private drafts. Visibility only moves forward: private, internal, public.</p>}
        </form>
        <div className="narrow">
          <PositionSizeHelper entry={v.entry} stop={v.stop} target={v.target} riskPct={v.riskPerTradePct} />
        </div>
      </div>
    </section>
  );
}
